import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box, Drawer, List, ListItemButton, ListItemIcon, ListItemText,
  Typography, Divider
} from '@mui/material';
import { useThemeMode } from '../contexts/ThemeContext';
import DashboardIcon from '@mui/icons-material/Dashboard';
import TuneIcon from '@mui/icons-material/Tune';
import HistoryIcon from '@mui/icons-material/History';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import PeopleIcon from '@mui/icons-material/People';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PaymentIcon from '@mui/icons-material/Payment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SecurityIcon from '@mui/icons-material/Security';
import BarChartIcon from '@mui/icons-material/BarChart';
import PsychologyIcon from '@mui/icons-material/Psychology';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ApprovalIcon from '@mui/icons-material/Approval';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PercentIcon from '@mui/icons-material/Percent';
import GavelIcon from '@mui/icons-material/Gavel';
import NoteIcon from '@mui/icons-material/StickyNote2';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';

const DRAWER_WIDTH = 220;

const adminMenuItems = [
  { label: 'Dashboard', path: '/admin', icon: <DashboardIcon fontSize="small" /> },
  { divider: true, label: 'Pricing' },
  { label: 'Plan Builder', path: '/admin/pricing', icon: <TuneIcon fontSize="small" /> },
  { label: 'Price History', path: '/admin/pricing/history', icon: <HistoryIcon fontSize="small" /> },
  { label: 'Bundles', path: '/admin/bundles', icon: <LocalOfferIcon fontSize="small" /> },
  { divider: true, label: 'Customers' },
  { label: 'Subscriptions', path: '/admin/subscriptions', icon: <PeopleIcon fontSize="small" /> },
  { label: 'Payments', path: '/admin/payments', icon: <PaymentIcon fontSize="small" /> },
  { label: 'Financial Ledger', path: '/admin/ledger', icon: <ReceiptLongIcon fontSize="small" /> },
  { label: 'Provider Fees', path: '/admin/fees', icon: <PaymentIcon fontSize="small" /> },
  { label: 'Customer Notes', path: '/admin/notes', icon: <NoteIcon fontSize="small" /> },
  { divider: true, label: 'Tax' },
  { label: 'Tax Rates', path: '/admin/tax/rates', icon: <PercentIcon fontSize="small" /> },
  { label: 'Exemptions', path: '/admin/tax/exemptions', icon: <GavelIcon fontSize="small" /> },
  { divider: true, label: 'Analytics' },
  { label: 'Usage Analytics', path: '/admin/usage', icon: <BarChartIcon fontSize="small" /> },
  { label: 'AI Usage', path: '/admin/ai-usage', icon: <PsychologyIcon fontSize="small" /> },
  { divider: true, label: 'Workflows' },
  { label: 'Workflow Monitor', path: '/admin/workflows', icon: <AccountTreeIcon fontSize="small" /> },
  { label: 'Workflows', path: '/admin/workflows/editor', icon: <EditNoteIcon fontSize="small" /> },
  { label: 'Approvals', path: '/admin/workflows/approvals', icon: <ApprovalIcon fontSize="small" /> },
  { divider: true, label: 'System' },
  { label: 'Products', path: '/admin/products', icon: <InventoryIcon fontSize="small" /> },
  { label: 'Discounts', path: '/admin/discounts', icon: <LocalOfferIcon fontSize="small" /> },
  { label: 'Admin Overrides', path: '/admin/overrides', icon: <AdminPanelSettingsIcon fontSize="small" /> },
  { label: 'Reconciliation', path: '/admin/reconciliation', icon: <CompareArrowsIcon fontSize="small" /> },
  { label: 'Audit Log', path: '/admin/audit', icon: <SecurityIcon fontSize="small" /> },
];

export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { isDark } = useThemeMode();

  // Match app design system (from resume-optimizer design-system.css)
  const sidebarBg = isDark ? '#162035' : '#1A2B4A';
  const textColor = isDark ? '#E8E0D0' : '#F5F0E8';
  const dividerColor = isDark ? 'rgba(46,74,122,0.4)' : 'rgba(245,240,232,0.1)';
  const labelColor = isDark ? '#B0A898' : 'rgba(245,240,232,0.4)';
  const hoverBg = isDark ? 'rgba(46,74,122,0.3)' : 'rgba(245,240,232,0.08)';

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <Box sx={{ display: 'flex', minHeight: 'calc(100vh - 64px)' }}>
      <Drawer
        variant="permanent"
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
            position: 'relative',
            backgroundColor: sidebarBg,
            color: textColor,
            borderRight: 'none'
          },
        }}
      >
        <Box sx={{ p: 2 }}>
          <Typography
            variant="overline"
            sx={{
              color: '#C8942A',
              fontWeight: 700,
              letterSpacing: '0.15em',
              fontSize: '0.65rem'
            }}
          >
            Administration
          </Typography>
        </Box>

        <List dense sx={{ px: 1 }}>
          {adminMenuItems.map((item, index) => {
            if (item.divider) {
              return (
                <Box key={index}>
                  <Divider sx={{ borderColor: dividerColor, my: 1 }} />
                  <Typography
                    variant="overline"
                    sx={{
                      px: 1.5,
                      fontSize: '0.6rem',
                      color: labelColor,
                      fontWeight: 600,
                      letterSpacing: '0.1em'
                    }}
                  >
                    {item.label}
                  </Typography>
                </Box>
              );
            }

            const active = isActive(item.path);

            return (
              <ListItemButton
                key={item.path}
                onClick={() => navigate(item.path)}
                selected={active}
                sx={{
                  borderRadius: 1,
                  mb: 0.25,
                  py: 0.5,
                  '&.Mui-selected': {
                    backgroundColor: 'rgba(200,148,42,0.15)',
                    color: '#C8942A',
                    '& .MuiListItemIcon-root': { color: '#C8942A' }
                  },
                  '&:hover': {
                    backgroundColor: hoverBg
                  },
                  '& .MuiListItemIcon-root': {
                    color: isDark ? 'rgba(200,148,42,0.4)' : 'rgba(245,240,232,0.5)',
                    minWidth: 32
                  }
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontSize: '0.8rem',
                    fontWeight: active ? 600 : 400
                  }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Drawer>

      {/* component="main" gives the content area a landmark: every admin page
          repeats its own name in the sidebar, so without it "Subscriptions" or
          "Payments" is ambiguous to a screen reader and to a test locator. */}
      <Box component="main" sx={{ flexGrow: 1, overflow: 'auto' }}>
        {children}
      </Box>
    </Box>
  );
}
